// Origen de una visita para las métricas internas (/admin/estadisticas).
// Misma lista que el server (VISIT_SOURCES en site-visit-daily.schema.ts).
// Solo viaja la categoría, nunca la URL de origen.
export type VisitSource =
  | "direct"
  | "search"
  | "social"
  | "whatsapp"
  | "email"
  | "ai"
  | "game_share"
  | "internal"
  | "other";

// Marca de los links que se comparten desde un juego (va en el #, así no
// llega a Netlify ni cambia la página que se sirve). "juega" en español,
// "play" en inglés.
export const GAME_SHARE_HASHES = ["#juega", "#play"];

const SEARCH = /(^|\.)(google|bing|duckduckgo|yahoo|ecosia|yandex|baidu|qwant|startpage)\.|^search\.brave\.com$|^googlequicksearchbox$|^com\.google\.android\.googlequicksearchbox$/;
const SOCIAL = /(^|\.)(instagram|facebook|fb|messenger|tiktok|twitter|x|threads|linkedin|reddit|youtube|pinterest|t)\.(com|me|co|net)$|^lm\.facebook\.com$|^com\.(instagram\.android|facebook\.katana|zhiliaoapp\.musically|twitter\.android)$/;
const WHATSAPP = /(^|\.)(whatsapp\.com|wa\.me)$|^com\.whatsapp(\.w4b)?$/;
const EMAIL = /^(mail\.google\.com|outlook\.(live|office|office365)\.com|mail\.yahoo\.com|mail\.proton\.me|com\.google\.android\.gm|com\.microsoft\.office\.outlook)$|(^|\.)mail\./;
const AI = /(^|\.)(chatgpt\.com|openai\.com|perplexity\.ai|claude\.ai|gemini\.google\.com|copilot\.microsoft\.com|you\.com)$/;

// Dominio (o paquete de la app de Android, en referrers android-app://).
function referrerHost(referrer: string): string | null {
  try {
    const url = new URL(referrer);
    if (url.protocol === "android-app:") return url.hostname.toLowerCase();
    return url.hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
}

// utm_source o ?ref= si vienen (links de campañas propias o ajenas).
function sourceFromParams(search: string): VisitSource | null {
  const params = new URLSearchParams(search);
  const tag = (params.get("utm_source") ?? params.get("ref") ?? "").toLowerCase();
  if (!tag) return null;
  if (/newsletter|email|mail|brevo/.test(tag)) return "email";
  if (/whatsapp|wa$/.test(tag)) return "whatsapp";
  if (/instagram|facebook|fb|tiktok|twitter|threads|linkedin|ig$/.test(tag)) return "social";
  if (/google|bing/.test(tag)) return "search";
  if (/chatgpt|openai|perplexity|gemini|claude|copilot/.test(tag)) return "ai";
  return "other";
}

export function classifyVisitSource(input: {
  referrer: string;
  hash: string;
  search: string;
  host: string;
}): VisitSource {
  if (GAME_SHARE_HASHES.includes(input.hash.toLowerCase())) return "game_share";
  const fromParams = sourceFromParams(input.search);
  if (fromParams) return fromParams;
  if (!input.referrer) return "direct";
  const host = referrerHost(input.referrer);
  if (!host) return "other";
  if (host === input.host.toLowerCase().replace(/^www\./, "")) return "internal";
  // Correo e IA antes que buscador: mail.google.com y gemini.google.com
  // también son de Google.
  if (WHATSAPP.test(host)) return "whatsapp";
  if (EMAIL.test(host)) return "email";
  if (AI.test(host)) return "ai";
  if (SEARCH.test(host)) return "search";
  if (SOCIAL.test(host)) return "social";
  return "other";
}
